import React from 'react';
import { View, ViewStyle } from 'react-native';

import i18n from '@/utils/i18n';
import { AnimatedSkeletonLoader } from '@usepepper/ui/atoms/animated-skeleton-loader';
import { createStyles } from '@/contexts/theme-context';

const EMPLOYEE_SUMMARY_CATEGORY_COUNT = 3;
const ROW_COUNT = 3;
const ROW_FADE_STEP = 0.33;

const getRowFadeStyle = (index: number): ViewStyle => ({
  opacity: 1 - index * ROW_FADE_STEP,
});

export const EmployeeListSkeleton = () => {
  const [styles] = useStyles();

  return (
    <View
      accessibilityLabel={i18n.t('sales_portal.employee_list.loading_label')}
      accessibilityState={{ busy: true }}
      style={styles.container}
    >
      <AnimatedSkeletonLoader borderRadius={4} height={28} width={140} />
      <View style={styles.mainLayout}>
        <View style={styles.contentColumn}>
          <AnimatedSkeletonLoader borderRadius={8} height={40} width="100%" />
          {Array.from({ length: ROW_COUNT }).map((_, index) => (
            <View key={index} style={[styles.row, getRowFadeStyle(index)]}>
              <AnimatedSkeletonLoader
                borderRadius={16}
                height={32}
                width={32}
              />
              <View style={styles.rowTextColumn}>
                <AnimatedSkeletonLoader
                  borderRadius={4}
                  height={20}
                  width={140}
                />
                <AnimatedSkeletonLoader
                  borderRadius={4}
                  height={20}
                  width={180}
                />
              </View>
              <AnimatedSkeletonLoader
                borderRadius={10}
                height={20}
                width={52}
              />
            </View>
          ))}
        </View>
        <View style={styles.sidebarColumn}>
          <View style={styles.sidebarCard}>
            <View style={styles.sidebarHeader}>
              <AnimatedSkeletonLoader
                borderRadius={6}
                height={16}
                width={120}
              />
              <AnimatedSkeletonLoader
                borderRadius={10}
                height={20}
                width={28}
              />
            </View>
            <View style={styles.sidebarCounts}>
              {Array.from({ length: EMPLOYEE_SUMMARY_CATEGORY_COUNT }).map(
                (_, index) => (
                  <View key={index} style={styles.sidebarCountRow}>
                    <AnimatedSkeletonLoader
                      borderRadius={6}
                      height={14}
                      width={100}
                    />
                    <AnimatedSkeletonLoader
                      borderRadius={6}
                      height={14}
                      width={20}
                    />
                  </View>
                ),
              )}
            </View>
            <View style={styles.sidebarInviteRow}>
              <AnimatedSkeletonLoader
                borderRadius={6}
                height={16}
                width={140}
              />
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
  },
  contentColumn: {
    flex: 3,
    gap: theme.sk.spacing.spaceMedium,
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  mainLayout: {
    flex: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.space3xlarge,
    marginTop: theme.sk.spacing.spaceMedium,
  },
  row: {
    alignItems: 'center',
    borderBottomColor: theme.sk.color.border.primary,
    borderBottomWidth: 1,
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
  rowTextColumn: {
    flex: 1,
    gap: theme.sk.spacing.spaceXsmall,
  },
  sidebarCard: {
    borderColor: theme.sk.color.border.primary,
    borderRadius: theme.sk.spacing.spaceSmall,
    borderWidth: 1,
  },
  sidebarColumn: {
    flex: 1,
  },
  sidebarCountRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
  },
  sidebarCounts: {
    gap: theme.sk.spacing.spaceSmall,
    paddingBottom: theme.sk.spacing.spaceMedium,
  },
  sidebarHeader: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  sidebarInviteRow: {
    borderTopColor: theme.sk.color.border.primary,
    borderTopWidth: 1,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
}));
